import {
  Box, Button, Flashbar, Form, FormField, Header, Input, Modal,
  Multiselect, SpaceBetween, Table, Textarea,
} from "@cloudscape-design/components";
import { useCallback, useEffect, useState } from "react";
import { getAuthToken } from "./auth";
import { apiFetch } from "./apiFetch";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { useDocumentTitle } from "./useDocumentTitle";

interface Stakeholder { id: number; documentId: string; firstName: string; lastName: string; }
interface StakeholderGroup {
  id: number; documentId: string; name: string; description: string;
  stakeholders: Stakeholder[]; projects: any[];
}

export default function StakeholderGroupsPage() {
  useDocumentTitle("Stakeholder Groups");
  const [groups, setGroups] = useState<StakeholderGroup[]>([]);
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<StakeholderGroup[]>([]);
  const [flash, setFlash] = useState<{ type: "success" | "error"; content: string } | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedStakeholders, setSelectedStakeholders] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const apiKey = getAuthToken();
  const selected = selectedItems[0];

  const fetchGroups = useCallback(() => {
    if (!apiKey) { setLoading(false); return; }
    setLoading(true);
    apiFetch(`/stakeholder-groups?populate[0]=stakeholders&populate[1]=projects`)
      .then((res) => { if (!res.ok) throw new Error(`API error: ${res.status}`); return res.json(); })
      .then((json) => setGroups(json.data.map((item: any) => ({
        id: item.id, documentId: item.documentId, name: item.name,
        description: item.description ?? "", stakeholders: item.stakeholders ?? [], projects: item.projects ?? [],
      }))))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [apiKey]);

  const fetchStakeholders = useCallback(() => {
    if (!apiKey) return;
    apiFetch(`/stakeholders`)
      .then((res) => res.json())
      .then((json) => setStakeholders(json.data.map((s: any) => ({ id: s.id, documentId: s.documentId, firstName: s.firstName, lastName: s.lastName }))))
      .catch(() => {});
  }, [apiKey]);

  useEffect(() => { fetchGroups(); fetchStakeholders(); }, [fetchGroups, fetchStakeholders]);

  const openCreate = () => {
    setName(""); setDescription(""); setSelectedStakeholders([]);
    setModalMode("create");
  };

  const openEdit = () => {
    if (!selected) return;
    setName(selected.name); setDescription(selected.description);
    setSelectedStakeholders(selected.stakeholders.map((s) => s.documentId));
    setModalMode("edit");
  };

  const handleSave = async () => {
    if (!apiKey) return;
    setSaving(true);
    try {
      const isEdit = modalMode === "edit";
      const url = isEdit ? `/stakeholder-groups/${selected.documentId}` : `/stakeholder-groups`;
      const res = await apiFetch(url, {
        method: isEdit ? "PUT" : "POST",
        body: JSON.stringify({ data: { name, description, stakeholders: selectedStakeholders } }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error?.message || `API error: ${res.status}`); }
      setFlash({ type: "success", content: isEdit ? "Stakeholder group updated" : "Stakeholder group created" });
      setModalMode(null); setSelectedItems([]); fetchGroups();
    } catch (e: any) { setFlash({ type: "error", content: e.message }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!apiKey || !selected) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/stakeholder-groups/${selected.documentId}`, {
        method: "DELETE",
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error?.message || `API error: ${res.status}`); }
      setFlash({ type: "success", content: "Stakeholder group deleted" });
      setDeleteVisible(false); setSelectedItems([]); fetchGroups();
    } catch (e: any) { setFlash({ type: "error", content: e.message }); }
    finally { setSaving(false); }
  };

  const stakeholderOptions = stakeholders.map((s) => ({ label: `${s.firstName} ${s.lastName}`, value: s.documentId }));

  return (
    <SpaceBetween size="l">
      {flash && (
        <Flashbar items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]} />
      )}
      <Table
        variant="full-page" loading={loading} items={groups} selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems as StakeholderGroup[])}
        trackBy="documentId"
        columnDefinitions={[
          { id: "name", header: "Name", cell: (item) => item.name },
          { id: "description", header: "Description", cell: (item) => item.description },
          { id: "members", header: "Members", cell: (item) => item.stakeholders.length },
          { id: "projects", header: "Projects", cell: (item) => item.projects.length },
        ]}
        header={
          <Header counter={`(${groups.length})`} actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button disabled={!selected} onClick={openEdit}>Edit</Button>
              <Button disabled={!selected} onClick={() => setDeleteVisible(true)}>Delete</Button>
              <Button variant="primary" onClick={openCreate}>Create stakeholder group</Button>
            </SpaceBetween>
          }>Stakeholder Groups</Header>
        }
        empty={
          <Box textAlign="center" color={error ? "text-status-error" : "text-body-secondary"}>
            {error ? `Error: ${error}` : !apiKey ? "No API key configured. Go to Settings to add one." : "No stakeholder groups found"}
          </Box>
        }
      />
      <Modal visible={modalMode !== null} onDismiss={() => setModalMode(null)}
        header={modalMode === "edit" ? "Edit stakeholder group" : "Create stakeholder group"}
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setModalMode(null)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>Submit</Button>
        </SpaceBetween></Box>}>
        <Form><SpaceBetween size="m">
          <FormField label="Name"><Input value={name} onChange={({ detail }) => setName(detail.value)} /></FormField>
          <FormField label="Description"><Textarea value={description} onChange={({ detail }) => setDescription(detail.value)} /></FormField>
          <FormField label="Stakeholders">
            <Multiselect selectedOptions={stakeholderOptions.filter((o) => selectedStakeholders.includes(o.value))}
              onChange={({ detail }) => setSelectedStakeholders(detail.selectedOptions.map((o) => o.value!))}
              options={stakeholderOptions} placeholder="Select stakeholders" />
          </FormField>
        </SpaceBetween></Form>
      </Modal>
      <DeleteConfirmModal
        visible={deleteVisible}
        header="Delete stakeholder group"
        itemName={selected?.name}
        loading={saving}
        onDismiss={() => setDeleteVisible(false)}
        onConfirm={handleDelete}
      />
    </SpaceBetween>
  );
}
